/**
 * @fileoverview i18n configuration and utility functions for internationalization.
 * This module sets up i18next with React integration and provides utilities for
 * dynamic translation loading.
 *
 * @module i18n
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getUilmFile } from '@/components/core/language-selector/services/language.service';
import { localResources } from './locales';

// declare custom type options so the return is always a string.
declare module 'i18next' {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

/**
 * Initialize i18next instance with default configuration.
 * - Sets English (US) as the default and fallback language
 * - Disables HTML escaping for interpolation
 * - Ensures null is never returned (always returns string)
 * - Seeded with local resources as fallback (remote translations overwrite on load)
 */
i18n.use(initReactI18next).init({
  lng: 'en-US',
  fallbackLng: 'en-US',
  interpolation: {
    escapeValue: false,
  },
  returnNull: false,
  resources: localResources,
});

/**
 * Loads and registers translations for a specific language and module.
 *
 * This function fetches translations from the API and adds them to the i18n instance.
 * It handles the translations in two ways:
 * 1. Adds them to the default 'translation' namespace for direct access
 * 2. Adds them to a module-specific namespace for organized access
 *
 * The function includes error handling and will not throw errors to avoid breaking the UI.
 *
 * @param {string} language - The language code to load translations for (e.g., 'en-US', 'fr-FR')
 * @param {string} moduleName - The module name to load translations for (e.g., 'common', 'dashboard')
 * @returns {Promise<void>} Resolves when translations are loaded and registered
 * @throws {never} Catches all errors internally to prevent UI disruption
 *
 * @example
 * // Load translations for the dashboard module in French
 * await loadTranslations('fr-FR', 'dashboard');
 */
/**
 * Set of every `(language, moduleName)` tuple `loadTranslations` has
 * successfully resolved during this session. We keep it so the UILM
 * browser-extension event listener (further down) can refetch the
 * SAME modules — anything else in i18next was never UILM-sourced and
 * shouldn't be touched.
 *
 * Stored as `lang::module` strings to keep equality cheap.
 */
const loadedUilmModules = new Set<string>();
const uilmModuleKey = (language: string, moduleName: string) => `${language}::${moduleName}`;

export const loadTranslations = async (language: string, moduleName: string): Promise<void> => {
  try {
    const translations = await getUilmFile({ language, moduleName });

    if (!translations) {
      return;
    }

    // Add the translations directly to i18n resources in the default namespace
    // This allows direct access to keys without namespace prefix
    i18n.addResourceBundle(
      language,
      'translation', // Default namespace for direct key access
      translations, // The JSON object with keys and translations
      true, // Deep merge
      true // Overwrite
    );

    // Also add to the specific module namespace for organization
    // This allows accessing keys with namespace prefix if needed
    i18n.addResourceBundle(language, moduleName, translations, true, true);

    loadedUilmModules.add(uilmModuleKey(language, moduleName));
  } catch (error) {
    console.error(`Failed to load translations for module ${moduleName}:`, error);
  }
};

/**
 * Refetch every UILM module previously loaded for the current language.
 * Called when the UILM browser extension signals that a key was edited
 * — `loadTranslations` deep-merges with overwrite, so edited values
 * replace what's currently in the bundle without disturbing untouched
 * keys.
 *
 * Other languages are intentionally left alone: the user is only
 * looking at one language at a time, and refetching every language
 * for every keystroke in the extension would be noisy.
 */
export const reloadLoadedTranslations = async (): Promise<void> => {
  const current = i18n.language;
  const tasks: Promise<void>[] = [];
  for (const tuple of loadedUilmModules) {
    const [lang, moduleName] = tuple.split('::');

    if (lang === current) {
      tasks.push(loadTranslations(lang, moduleName));
    }
  }
  await Promise.all(tasks);
};

/**
 * ---------------------------------------------------------------------------
 * Key Mode Toggle Logic
 * ---------------------------------------------------------------------------
 * The following code enables dynamic toggling between displaying translation
 * **values** (default) and **keys** (key-mode) at runtime. The toggle is driven
 * by messages sent from a browser extension via `window.postMessage`.
 *
 * Expected message format:
 *   { action: 'keymode', keymode: boolean, defaultLang?: string }
 *
 * When `keymode` is `true`, all calls to `i18n.t()` will return the **key**
 * instead of the translated value. This is achieved by monkey-patching the
 * `i18n.t` function while preserving its original behaviour for normal mode.
 *
 * Renders are refreshed by emitting a `languageChanged` event, which the
 * `react-i18next` provider already listens to for triggering re-renders.
 * ---------------------------------------------------------------------------
 */

declare global {
  interface Window {
    __i18nKeyMode?: boolean;
  }
}

// Initialise global flag
if (typeof window !== 'undefined') {
  window.__i18nKeyMode = false;
}

// Preserve original translator
const originalT = i18n.t.bind(i18n);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(i18n as any).t = (key: string | string[], options?: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window.__i18nKeyMode) {
    if (Array.isArray(key)) return key[0];
    return key;
  }
  return (originalT as any)(key, options);
};

// Listen for messages coming from the browser extension
if (typeof window !== 'undefined') {
  window.addEventListener('message', async (event) => {
    // console.log('message received in i18n module:', event);
    if (event.source !== window) return; // Ignore messages from iframes or other sources
    if (event.origin !== window.location.origin) return; // Prevent cross-origin injections

    const { data } = event;
    if (!data || typeof data !== 'object') return;

    if (data.type === 'REFETCH_TRANSLATION') {
      await reloadLoadedTranslations();
      (i18n as any).emit('languageChanged', i18n.language);
      return;
    }

    const { action, keymode } = data as { action?: string; keymode?: boolean };
    if (action === 'keymode' && typeof keymode === 'boolean') {
      window.__i18nKeyMode = keymode;

      // The extension emits this same event after a key edit, not only on
      // toggle, so refetch every loaded UILM module for the current
      // language. Edits land in the UI without a page reload; untouched
      // keys are unchanged because `loadTranslations` deep-merges with
      // overwrite (new value wins per key, others stay).
      // await reloadLoadedTranslations();

      // Force re-render — both for the keymode flip AND for any resource
      // values the refetch may have replaced. Emitting unconditionally
      // covers both signals with one trigger.
      (i18n as any).emit('languageChanged', i18n.language);
    }
  });
}

export default i18n;
